/**
 * Aufrufe der Mailverwaltung -- F3-01, F3-04, F3-06.
 *
 * **Das Postfachkennwort geht nur hinein, nie heraus.** Der Server legt es
 * verschluesselt ab und gibt es in keiner Antwort zurueck (docs/03,
 * Abschnitt 12). Wer es aendern will, gibt ein neues ein; wer nichts eingibt,
 * laesst das alte stehen.
 */

import type { components } from '../../api/schema';
import { request } from '../../lib/apiClient';

export type MailAccount = components['schemas']['MailAccountRead'];
export type MailFilter = components['schemas']['MailFilterRead'];
export type Signature = components['schemas']['SignatureRead'];
export type Announcement = components['schemas']['AnnouncementRead'];
export type EmailTemplate = components['schemas']['EmailTemplateRead'];
export type OutboundMessage = components['schemas']['OutboundMessageRead'];
export type SendResult = components['schemas']['SendResultRead'];
export type InboundMessage = components['schemas']['InboundMessageRead'];
export type InboundMessageDetail = components['schemas']['InboundMessageDetail'];
export type InboundMessageList = components['schemas']['InboundMessageList'];
export type InboundStatus = components['schemas']['InboundStatus'];
export type InboundCount = components['schemas']['InboundCount'];

export function listAccounts(): Promise<MailAccount[]> {
  return request<MailAccount[]>('/mail-accounts');
}

export function createAccount(payload: Record<string, unknown>): Promise<MailAccount> {
  return request<MailAccount>('/mail-accounts', { method: 'POST', body: payload });
}

export function updateAccount(
  accountId: string,
  payload: Record<string, unknown>,
): Promise<MailAccount> {
  return request<MailAccount>(`/mail-accounts/${accountId}`, { method: 'PATCH', body: payload });
}

export function deleteAccount(accountId: string): Promise<undefined> {
  return request<undefined>(`/mail-accounts/${accountId}`, { method: 'DELETE' });
}

/** Holt sofort ab, statt auf den Taktgeber zu warten -- fuer die Einrichtung. */
export function pollAccounts(): Promise<unknown> {
  return request<unknown>('/mail-accounts/actions/poll', { method: 'POST' });
}

export function listFilters(): Promise<MailFilter[]> {
  return request<MailFilter[]>('/mail-filters');
}

export function createFilter(payload: Record<string, unknown>): Promise<MailFilter> {
  return request<MailFilter>('/mail-filters', { method: 'POST', body: payload });
}

export function deleteFilter(filterId: string): Promise<undefined> {
  return request<undefined>(`/mail-filters/${filterId}`, { method: 'DELETE' });
}

export function listSignatures(): Promise<Signature[]> {
  return request<Signature[]>('/signatures');
}

export function createSignature(payload: Record<string, unknown>): Promise<Signature> {
  return request<Signature>('/signatures', { method: 'POST', body: payload });
}

export function deleteSignature(signatureId: string): Promise<undefined> {
  return request<undefined>(`/signatures/${signatureId}`, { method: 'DELETE' });
}

/**
 * Ankuendigungen und Stoerungsbanner -- F3-10, K-56.
 *
 * ``listAnnouncements`` liefert **alle**, auch abgelaufene und noch nicht
 * begonnene: In der Verwaltung will man sehen, was war und was kommt.
 * ``/announcements/active`` -- die Abfrage des Banners -- liefert nur, was
 * gerade zu zeigen ist.
 */
export function listAnnouncements(): Promise<Announcement[]> {
  return request<Announcement[]>('/announcements');
}

export function createAnnouncement(payload: Record<string, unknown>): Promise<Announcement> {
  return request<Announcement>('/announcements', { method: 'POST', body: payload });
}

export function updateAnnouncement(
  announcementId: string,
  payload: Record<string, unknown>,
): Promise<Announcement> {
  return request<Announcement>(`/announcements/${announcementId}`, {
    method: 'PATCH',
    body: payload,
  });
}

export function deleteAnnouncement(announcementId: string): Promise<undefined> {
  return request<undefined>(`/announcements/${announcementId}`, { method: 'DELETE' });
}

/**
 * Mailvorlagen -- F3-05.
 *
 * Der Anlass steckt im Schluessel: ``ticket_created`` ist die
 * Eingangsbestaetigung. Fehlt die Vorlage, greift ein eingebauter Text --
 * ohne diesen Rueckfall stuende bei einem Mandanten ohne gepflegte Vorlagen
 * der Kanal still, und das faellt niemandem auf, weil eine ausbleibende Mail
 * keine Fehlermeldung erzeugt.
 */
export function listEmailTemplates(): Promise<EmailTemplate[]> {
  return request<EmailTemplate[]>('/email-templates');
}

export function createEmailTemplate(payload: Record<string, unknown>): Promise<EmailTemplate> {
  return request<EmailTemplate>('/email-templates', { method: 'POST', body: payload });
}

export function updateEmailTemplate(
  templateId: string,
  payload: Record<string, unknown>,
): Promise<EmailTemplate> {
  return request<EmailTemplate>(`/email-templates/${templateId}`, {
    method: 'PATCH',
    body: payload,
  });
}

export function deleteEmailTemplate(templateId: string): Promise<undefined> {
  return request<undefined>(`/email-templates/${templateId}`, { method: 'DELETE' });
}

export type MailEvent = components['schemas']['MailEventRead'];
export type MailTemplatePreview = components['schemas']['MailTemplatePreviewRead'];

/** Die Anlaesse, zu denen eine Mail entsteht, mit ihrer Vorlage -- F3-21. */
export function listMailEvents(): Promise<MailEvent[]> {
  return request<MailEvent[]>('/mail-events');
}

/** Weist einem Anlass eine Vorlage zu -- ``null`` heisst "Nicht versenden". */
export function assignMailEvent(eventKey: string, templateId: string | null): Promise<MailEvent> {
  return request<MailEvent>(`/mail-events/${eventKey}`, {
    method: 'PUT',
    body: { template_id: templateId },
  });
}

/** Fuellt eine Vorlage mit den Beispielwerten eines Anlasses. Speichert nichts. */
export function previewEmailTemplate(payload: {
  event_key: string;
  subject: string;
  body_html: string;
}): Promise<MailTemplatePreview> {
  return request<MailTemplatePreview>('/email-templates/preview', {
    method: 'POST',
    body: payload,
  });
}

/**
 * Die Versandwarteschlange -- F3-07.
 *
 * Wer hier hineinschaut, sucht nach dem, was **nicht** hinausging. Deshalb
 * steht der Fehlertext an der Zeile und nicht in einem Protokoll, das nur
 * auf dem Server liegt.
 */
export function listOutbound(): Promise<OutboundMessage[]> {
  return request<OutboundMessage[]>('/outbound');
}

export function sendOutbound(): Promise<SendResult> {
  return request<SendResult>('/outbound/actions/send', { method: 'POST' });
}

/**
 * Die Anfrage-Queue -- F5-03.
 *
 * Mails, aus denen kein Vorgang wurde, weil sich der Absender keinem Kunden
 * zuordnen liess. Vor diesem Feature waren sie unsichtbar: Der Abruf hatte
 * sie gesehen, die Ticketanlage hatte abgelehnt, und die Kennung in
 * ``inbound_messages`` sorgte dafuer, dass sie beim naechsten Abruf nicht
 * wiederkamen. Am 15.09.2026 lagen 141 davon.
 */
export function listInbound(status: InboundStatus): Promise<InboundMessageList> {
  return request<InboundMessageList>(`/mail/inbound?status=${status}&limit=200`);
}

/**
 * Nur die Zahl -- fuer die Blase am Reiter in der Ticketliste.
 *
 * Und nicht die Liste mit ``items.length``: Die Anfragen tragen den Text der
 * Mail, und den in der Ticketliste zu laden hiesse, bei jedem Aufruf ein
 * paar hundert Kilobyte fremder Post zu holen, um eine einstellige Zahl
 * anzuzeigen.
 */
export function countInbound(status: InboundStatus = 'open'): Promise<InboundCount> {
  return request<InboundCount>(`/mail/inbound/count?status=${status}`);
}

/**
 * Eine Anfrage mit dem vollstaendigen Text.
 *
 * Die Liste traegt nur den Anfang. Wer eine Mail einem Kunden zuordnen soll,
 * findet den Namen oft erst in der Signatur oder im zitierten Verlauf --
 * also genau dort, wo die Vorschau aufhoert.
 */
export function getInbound(inboundId: string): Promise<InboundMessageDetail> {
  return request<InboundMessageDetail>(`/mail/inbound/${inboundId}`);
}

export function assignInbound(
  inboundId: string,
  payload: { customer_id: string; contact_id?: string | null; create_contact?: boolean },
): Promise<InboundMessage> {
  return request<InboundMessage>(`/mail/inbound/${inboundId}/assign`, {
    method: 'POST',
    body: payload,
  });
}

export function rejectInbound(
  inboundId: string,
  payload: { reason?: string | null },
): Promise<InboundMessage> {
  return request<InboundMessage>(`/mail/inbound/${inboundId}/reject`, {
    method: 'POST',
    body: payload,
  });
}
